import React, { useEffect, useState } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import {
  postLoginBusinessesSelector,
  allBusinessLenderStatusesSelector,
  routingDecisionSelector,
  defaultBusinessIdSelector,
  defaultProductKeySelector,
  inProgressBusinessIdSelector,
  pageTitleSubmit,
  useIsMobile
} from '../../recoil';
import PropTypes from 'prop-types';
import {
  useLocation,
  useNavigate
} from 'react-router-dom';
import {pageNames} from '../../helpers';
import { extractPathInfo } from '../../helpers/extractPathInfo';
import {
  SelectedBusiness,
  Business,
  LogoutSvg,
  UpArrow,
  DownArrow
} from '../../assets';
import { StaticRoutes } from '../../Router';
import { SIDEBAR_ROUTE_SEGMENTS } from '../../constants/RouteSegments';
import { TootipComp } from '../Tooltip/TooltipComp';
import './sidebar.css';
import { useTealium } from '../../tealium/useTealium';

const BusinessName = ({ name, isMobile }) => {
  if (isMobile) {
    return <span className='text-break'>{name}</span>;
  }
  return <TootipComp text={name} />;
};

BusinessName.propTypes = {
  name: PropTypes.string,
  isMobile: PropTypes.bool
};

const ProductName = ({ product, isMobile }) => {
  const displayName = product?.productCode === 'RCF' ? 'Cash Flow Facility' : product?.product;
  if (isMobile) {
    return <span className='ml-10 text-truncate'><span className='text-break'>{displayName}</span></span>;
  }
  return <TootipComp text={displayName} />;
};

ProductName.propTypes = {
  product: PropTypes.shape({
    productCode: PropTypes.string,
    product: PropTypes.string
  }),
  isMobile: PropTypes.bool
};

const resolveNavigationTarget = (routingDecision, businessId, defaultProductKey, allBusinessLenderStatuses) => {
  if (routingDecision === 'ACCOUNTS') {
    const productEntry = allBusinessLenderStatuses?.[businessId]?.[defaultProductKey];
    const appId = productEntry?.applicationReference ?? productEntry?.applicationId;
    return appId ? `/accounts/${appId}` : null;
  }
  if (routingDecision === 'APPLICATIONS') {
    return `/applications/${businessId}`;
  }
  return null;
};

const getProductsForBusiness = (allBusinessLenderStatuses, businessId) => {
  const businessEntry = allBusinessLenderStatuses?.[businessId];
  if (!businessEntry) {
    return [];
  }

  return Object.keys(businessEntry)
    .filter(key => key.includes('#'))
    .map(key => ({
      applicationId: businessEntry[key].applicationReference ?? businessEntry[key].applicationId,
      product: businessEntry[key].productDisplayName ?? businessEntry[key].productName,
      productCode: businessEntry[key].productCode,
      status: businessEntry[key].status,
      productKey: key
    }));
};

export const Sidebar = ({ setSideNavOpen = () => { } }) => {
  const isMobile = useRecoilValue(useIsMobile);

  const businesses = useRecoilValue(postLoginBusinessesSelector);
  const allBusinessLenderStatuses = useRecoilValue(allBusinessLenderStatusesSelector);
  const routingDecision = useRecoilValue(routingDecisionSelector);
  const defaultBusinessId = useRecoilValue(defaultBusinessIdSelector);
  const defaultProductKey = useRecoilValue(defaultProductKeySelector);
  const inProgressBusinessId = useRecoilValue(inProgressBusinessIdSelector);
  const [ , setPageTitle] = useRecoilState(pageTitleSubmit);
  const navigate = useNavigate();
  const location = useLocation();
  const { track, events } = useTealium();

  const effectiveBusinesses = businesses?.length ? businesses : [];

  const [listBusiness, setBusiness] = useState([]);
  const [listProduct, setProduct] = useState([]);
  const [isMyBusinessesOpen, setMyBusinessesOpen] = useState(false);

  const navigateTo = (path) =>
    navigate(path, {
      state: {
        returnPath: location.pathname
      }
    });

  const closeMenu = () => {
    if (isMobile) {
      setSideNavOpen(false);
    }
  };

  const resolveBusinessIdFromRoutingDecision = () => {
    switch (routingDecision) {
      case 'ACCOUNTS':
        return defaultBusinessId ?? null;
      case 'APPLICATIONS':
        return inProgressBusinessId ?? null;
      default:
        return null;
    }
  };

  const resolveSelectedProductKeyFromRoutingDecision = () => {
    if (routingDecision === 'ACCOUNTS' && defaultProductKey) {
      return defaultProductKey;
    }
    return null;
  };

  const findBusinessIdByApplicationId = (applicationId) => {
    for (const [bizId, entry] of Object.entries(allBusinessLenderStatuses || {})) {
      const match = Object.keys(entry)
        .filter(k => k.includes('#'))
        .find(key => (entry[key].applicationReference ?? entry[key].applicationId) === applicationId);
      if (match) { return bizId; }
    }
    return null;
  };

  const buildBusinessList = (businessId, resourceType, pendingProductKey) => {
    const quoteAvailableStatus = allBusinessLenderStatuses?.[businessId]?.hasQuoteAvailable ?? false;
    const isMyApplicationsRoute = resourceType === 'applications' && !pendingProductKey;
    return effectiveBusinesses?.map(businessItem => ({
      ...businessItem,
      isSelected: businessItem?.businessId === businessId,
      isQuoteAvailable: quoteAvailableStatus,
      isMyApplicationSelected: isMyApplicationsRoute && businessItem?.businessId === businessId,
      isFundingQuoteSelected: resourceType === 'quotes' && businessItem?.businessId === businessId,
      isProductCatalogueSelected: resourceType === 'products' && businessItem?.businessId === businessId
    }));
  };

  // Renders every business unselected/collapsed — used as a fallback so the sidebar
  // is never blank when the URL id can't be resolved to a business.
  const buildFallbackBusinessList = () =>
    effectiveBusinesses?.map(businessItem => ({
      ...businessItem,
      isSelected: false,
      isQuoteAvailable: allBusinessLenderStatuses?.[businessItem?.businessId]?.hasQuoteAvailable ?? false,
      isMyApplicationSelected: false,
      isFundingQuoteSelected: false,
      isProductCatalogueSelected: false
    })) ?? [];

  const buildProductList = (businessId, applicationId, pendingProductKey, resourceType) => {
    const productList = getProductsForBusiness(allBusinessLenderStatuses, businessId);
    const urlHasExplicitSelection = !!applicationId || !!pendingProductKey ||
      resourceType === 'products' || resourceType === 'applications' || resourceType === 'quotes';
    const selectedProductKey = urlHasExplicitSelection
      ? (pendingProductKey ?? null)
      : resolveSelectedProductKeyFromRoutingDecision();

    return productList.map(product => {
      const isSelectedByAppId = applicationId && product?.applicationId === applicationId;
      const isSelectedByProductKey = !applicationId && selectedProductKey && product?.productKey === selectedProductKey;
      if (isSelectedByAppId || isSelectedByProductKey) {
        setPageTitle({ productName: pageNames[product?.product] });
      }
      return { ...product, isSelected: !!(isSelectedByAppId || isSelectedByProductKey) };
    });
  };

  const initializeSideNav = () => {
    let { resourceType, businessId, applicationId } = extractPathInfo(location?.pathname);

    if (applicationId && !businessId) {
      businessId = findBusinessIdByApplicationId(applicationId);
    }

    if (!businessId) {
      businessId = resolveBusinessIdFromRoutingDecision();
      if (businessId) {
        const target = resolveNavigationTarget(routingDecision, businessId, defaultProductKey, allBusinessLenderStatuses);
        if (target) {
          navigate(target, { replace: true });
          return;
        }
      }
    }

    if (!businessId) {
      setMyBusinessesOpen(true);
      setBusiness(buildFallbackBusinessList());
      setProduct([]);
      return;
    }

    const pendingProductKey = location?.state?.pendingProductKey ?? null;

    setMyBusinessesOpen(true);
    setBusiness(buildBusinessList(businessId, resourceType, pendingProductKey));
    setProduct(buildProductList(businessId, applicationId, pendingProductKey, resourceType));
  };

  useEffect(() => {
    const segments = location?.pathname?.split('/').filter(Boolean);
    const [resourceType] = segments;
    if (SIDEBAR_ROUTE_SEGMENTS.includes(resourceType)) {
      initializeSideNav();
    }
  }, [location.pathname, businesses, allBusinessLenderStatuses, routingDecision, defaultBusinessId, inProgressBusinessId, defaultProductKey]);

  useEffect(() => {
    track(events.menubar.pageView());
  }, []);

  useEffect(() => {
    if (listBusiness && allBusinessLenderStatuses && listBusiness.length > 0) {
      const needsUpdate = listBusiness.some(businessItem => {
        const currentStatus = businessItem.isQuoteAvailable ?? false;
        const newStatus = allBusinessLenderStatuses?.[businessItem?.businessId]?.hasQuoteAvailable ?? false;
        return currentStatus !== newStatus;
      });

      if (needsUpdate) {
        const updatedBusinessList = listBusiness.map(businessItem => {
          const quoteAvailableStatus = allBusinessLenderStatuses?.[businessItem?.businessId]?.hasQuoteAvailable ?? false;
          return {
            ...businessItem,
            isQuoteAvailable: quoteAvailableStatus
          };
        });
        setBusiness(updatedBusinessList);
      }
    }
  }, [allBusinessLenderStatuses, listBusiness]);

  // --- Event handlers ---

  const handleClickBusines = () => {
    setMyBusinessesOpen(!isMyBusinessesOpen);
    // setIsNewProductOpen(false);
    track(events.menubar.menubarClick('my business clicked'));
  };

  const handleSelectBusiness = (selected, businessId) => {
    const quoteAvailableStatus = allBusinessLenderStatuses?.[businessId]?.hasQuoteAvailable ?? false;
    const updatedBusinessList = listBusiness?.map(businessItem => {
      if (businessItem?.businessId === businessId) {
        return { ...businessItem, isSelected: !selected, isMyApplicationSelected: false, isFundingQuoteSelected: false, isProductCatalogueSelected: false, isQuoteAvailable: quoteAvailableStatus };
      }
      return { ...businessItem, isSelected: false, isMyApplicationSelected: false, isFundingQuoteSelected: false, isProductCatalogueSelected: false, isQuoteAvailable: quoteAvailableStatus };
    });
    setBusiness(updatedBusinessList);
    // setIsNewProductOpen(false);
    setProduct(getProductsForBusiness(allBusinessLenderStatuses, businessId));
    track(events.menubar.menubarClick('business clicked'));
  };

  const handleSelectApplication = (businessId) => {
    setBusiness(listBusiness?.map(b => ({ ...b, isMyApplicationSelected: b.businessId === businessId, isFundingQuoteSelected: false, isProductCatalogueSelected: false })));
    setProduct(listProduct?.map(p => ({ ...p, isSelected: false })));
    track(events.menubar.menubarClick('my application clicked'));
    navigateTo(`/applications/${businessId}`);
    closeMenu();
  };

  const handleSelectFundingQuote = (businessId) => {
    setBusiness(listBusiness?.map(b => ({ ...b, isMyApplicationSelected: false, isFundingQuoteSelected: b.businessId === businessId, isProductCatalogueSelected: false })));
    setProduct(listProduct?.map(p => ({ ...p, isSelected: false })));
    track(events.menubar.menubarClick('funcding quote clicked'));
    navigateTo(`/quotes/${businessId}`);
    closeMenu();
  };

  const handleSelectProductCatalogue = (businessId) => {
    setBusiness(listBusiness?.map(b => ({
      ...b,
      isMyApplicationSelected: false,
      isFundingQuoteSelected: false,
      isProductCatalogueSelected: b?.businessId === businessId
    })));
    setProduct(listProduct?.map(product => ({ ...product, isSelected: false })));
    track(events.menubar.menubarClick('explore new products clicked'));
    navigateTo(`/products/${businessId}`);
    closeMenu();
  };

  const handleSelectProduct = (selectedProduct) => {
    const updatedProducts = listProduct.map(p => ({
      ...p,
      isSelected: p?.productKey === selectedProduct.productKey
    }));
    const productName = pageNames[selectedProduct?.product];
    setPageTitle({pageTitle: productName});
    setBusiness(listBusiness?.map(b => ({ ...b, isMyApplicationSelected: false, isFundingQuoteSelected: false, isProductCatalogueSelected: false })));
    // setIsNewProductOpen(false);
    setProduct(updatedProducts);
    track(events.menubar.menubarClick('product clicked'));
    if (selectedProduct.applicationId) {
      navigateTo(`/accounts/${selectedProduct.applicationId}`);
    } else {
      const ownerBusiness = listBusiness?.find(b => b.isSelected);
      if (ownerBusiness) {
        navigate(`/applications/${ownerBusiness.businessId}`, {
          state: {
            returnPath: location.pathname,
            pendingProductKey: selectedProduct.productKey
          }
        });
      }
    }
    closeMenu();
  };

  const containerClass = isMobile ? 'mobile-sidebar' : 'sidebar';
  const ContainerTag = isMobile ? 'div' : 'aside';
  const businessDividerClass = isMobile ? 'divider ml-20 wd-106' : 'divider ml-20 wd-109';
  const productDividerClass = isMobile ? 'divider ml-40 wd-114' : 'divider ml-40 wd-119';

  return (
    <ContainerTag className={containerClass}>
      <ul className="menu">

        <li>
          <button
            className={isMyBusinessesOpen ? 'selected-menu pl-0' : 'menu-item'}
            onClick={() => handleClickBusines()}
          >
            {
              isMyBusinessesOpen ?
                <span className='left-border' >|</span> : ''}
            {isMyBusinessesOpen ? <img src={SelectedBusiness} alt='' /> : <img src={Business} alt='' />}  <span className='ml-10'>My Businesses</span>
            {isMyBusinessesOpen ?
              <img className='icon' src={UpArrow} alt='' /> :
              <img className='icon' src={DownArrow} alt='' />
            }
          </button>
          <div className='divider' />
          {isMyBusinessesOpen && (
            <ul className="submenu">
              {listBusiness?.map((business, index) => {
                return (<li key={`busines${index}`}>
                  <button
                    className={business?.isSelected ? 'selected-menu pl-0 pb-14 pt-12' : 'menu-item pl-0'}
                    onClick={() => handleSelectBusiness(business?.isSelected, business?.businessId)}
                  >
                    <BusinessName name={business?.businessName} isMobile={isMobile} />
                    {business?.isSelected ?
                      <img className='icon' src={UpArrow} alt='' /> :
                      <img className='icon' src={DownArrow} alt='' />
                    }
                  </button>
                  <div className={businessDividerClass} />
                  {business?.isSelected && (
                    <ul className="submenu">
                      {business?.isQuoteAvailable &&
                      <>
                        <button className={business?.isFundingQuoteSelected ? 'selected-menu pl-0 pb-14 pt-12' : 'menu-item pl-0'}
                          onClick={() => handleSelectFundingQuote(business?.businessId)}
                        ><span className='application-list-item-style'>.</span > <span className='ml-10'> Funding Quote</span></button>
                        <div className={productDividerClass} />
                      </>
                      }
                      <button className={business?.isMyApplicationSelected ? 'selected-menu pl-0 pb-14 pt-12' : 'menu-item pl-0'}
                        onClick={() => handleSelectApplication(business?.businessId)}
                      ><span className='application-list-item-style'>.</span > <span className='ml-10'> My Applications</span></button>
                      <div className={productDividerClass} />
                      {
                        listProduct?.map((product, index) => {
                          return (
                            <li key={`product${index}`}>
                              { product?.product && (
                                <>
                                  <button className={product.isSelected ? 'selected-menu pl-0 pb-14 pt-12' : 'menu-item pl-0'}
                                    onClick={() => handleSelectProduct(product)}
                                  ><span className='application-list-item-style'>.</span>
                                    <ProductName product={product} isMobile={isMobile} />
                                  </button>
                                  <div className={productDividerClass} />
                                </>
                              )
                              }
                            </li >
                          );
                        })
                      }
                      <button
                        className={business?.isProductCatalogueSelected ? 'selected-menu pl-0 pb-14 pt-12' : 'menu-item pl-0'}
                        onClick={() => handleSelectProductCatalogue(business?.businessId)}
                      >
                        <span className='application-list-item-style'>.</span>
                        <span className='ml-10'> Explore new products</span>
                      </button>
                      <div className={productDividerClass} />
                    </ul >
                  )}
                </li >);
              })}
            </ul >
          )}
        </li >
        <li>
          <button className="bottom-menu-item" onClick={() => {track(events.menubar.menubarClick('logout clicked'));navigateTo(StaticRoutes.logout);closeMenu();}} ><img src={LogoutSvg} alt='' /><span className='ml-10'>Logout</span></button><div className='divider' />
        </li>
      </ul >
    </ContainerTag >
  );
};

export const MobileSidebar = Sidebar;

Sidebar.propTypes = {
  setSideNavOpen: PropTypes.func
};
